<script setup lang="ts">
import { computed } from 'vue'

import InsightActionRowTable from './InsightActionRowTable.vue'

import { useInsightActionBanners } from '../composables/useInsightActionBanners'
import { useInsightActionCommands } from '../composables/useInsightActionCommands'
import { useInsightActionSelection } from '../composables/useInsightActionSelection'
import { useReasonText } from '../composables/useReasonText'
import { useInsightActionStore } from '../stores/insightActionStore'

import { humanizeKey } from '../service/labels'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui/logo'

const { t } = useI18n()
const store = useInsightActionStore()
const selection = useInsightActionSelection()
const banners = useInsightActionBanners()
const commands = useInsightActionCommands()
const { chipTail, requestError } = useReasonText()

const loadError = computed(() =>
  !selection.group.value && store.entry.errorCode ? requestError(store.entry.errorCode) : null,
)

function channelName(key: string): string {
  return humanizeKey(key)
}

function channelLogo(key: string): { src: string } | Record<string, never> {
  const src = resolveStaticPlatformLogo(key)
  return src ? { src } : {}
}

const PILL = 'border transition-colors'
const PILL_ON = 'border-primary bg-primary-subtle text-primary'
const PILL_OFF = 'border-border text-muted hover:border-primary-subtle'

function pillClass(active: boolean): string {
  return `${PILL} ${active ? PILL_ON : PILL_OFF}`
}

const sentence = computed(() => {
  const group = selection.group.value
  if (!group) return ''
  const names = selection.channels.value
    .filter((channel) => channel.actionable > 0)
    .map((channel) => channelName(channel.key))
  const where =
    names.length === 1
      ? t('insightActions.select.whereOne', { channel: names[0] ?? '' })
      : names.length
        ? t('insightActions.select.whereMany', { channels: names.join(', ') })
        : ''
  return t('insightActions.select.sentence', {
    label: group.label,
    where,
    undo: group.revertible ? t('insightActions.select.undoYes') : t('insightActions.select.undoNo'),
  })
})

function addressedCount(key: string): number {
  const group = selection.groups.value.find((candidate) => candidate.key === key)
  return (group?.rows ?? []).filter((row) => row.reasonCode !== 'filtered_out').length
}

function chipLabel(channel: { actionable: number; total: number; reason: string | null }): string {
  if (channel.actionable === channel.total) return t('insightActions.select.chip.canApply')
  if (channel.actionable > 0) {
    return t('insightActions.select.chip.someCanApply', { count: channel.actionable })
  }
  return chipTail(channel.reason)
}
</script>

<template>
  <div class="insight-action-select grid gap-md min-w-0">
    <Alert v-if="loadError" tone="danger" :title="loadError.message" data-testid="select-error">
      <Button variant="outline" size="xs" @click="commands.refreshPreview()">
        {{ t('insightActions.select.retry') }}
      </Button>
    </Alert>

    <template v-else-if="selection.group.value">
      <div v-if="selection.groups.value.length > 1" class="flex flex-wrap gap-xs">
        <button
          v-for="group in selection.groups.value"
          :key="group.key"
          type="button"
          class="rounded-md px-sm py-2xs text-xs font-semibold"
          :class="pillClass(group.key === selection.group.value.key)"
          :aria-pressed="group.key === selection.group.value.key"
          :data-testid="`group-${group.key}`"
          @click="store.setGroup(group.key)"
        >
          {{ group.label }}
          <span class="ms-xs rounded-sm bg-surface-sunken px-2xs py-2xs text-2xs">{{
            addressedCount(group.key)
          }}</span>
        </button>
      </div>

      <p v-if="sentence" class="m-0 text-sm leading-relaxed">{{ sentence }}</p>

      <div v-if="selection.channels.value.length" class="flex flex-wrap items-center gap-xs">
        <button
          v-for="channel in selection.channels.value"
          :key="channel.key"
          type="button"
          class="inline-flex items-center gap-2xs rounded-full px-sm py-2xs text-xs disabled:cursor-default"
          :class="pillClass(store.channelFilter === channel.key)"
          :disabled="!selection.channelFilterEnabled.value"
          :aria-pressed="store.channelFilter === channel.key"
          :title="
            selection.channelFilterEnabled.value
              ? ''
              : t('insightActions.select.channelFilterUnavailable')
          "
          :data-testid="`channel-${channel.key}`"
          @click="store.setChannelFilter(channel.key)"
        >
          <PlatformLogo
            :platform="channelName(channel.key)"
            v-bind="channelLogo(channel.key)"
            :size="14"
          />
          <span class="text-fg">{{ channelName(channel.key) }}</span>
          <b class="text-fg">{{ channel.total }}</b>
          <span class="text-muted">· {{ chipLabel(channel) }}</span>
        </button>
      </div>

      <Alert
        v-for="banner in banners"
        :key="banner.key"
        :tone="banner.tone"
        :title="banner.text"
        :data-testid="`banner-${banner.key}`"
      />

      <p v-if="selection.allBlocked.value" class="m-0 text-xs text-muted">
        {{ t('insightActions.select.allDeferred') }}
      </p>

      <div v-else class="flex flex-wrap items-center justify-between gap-xs text-xs">
        <div class="flex items-center gap-xs">
          <button
            type="button"
            class="rounded-full px-sm py-2xs"
            :class="pillClass(store.onlyApplicable)"
            :aria-pressed="store.onlyApplicable"
            data-testid="only-applicable"
            @click="store.setOnlyApplicable(!store.onlyApplicable)"
          >
            {{ t('insightActions.select.onlyApplicable') }}
          </button>
          <span class="text-muted">
            {{
              t('insightActions.select.canApplyCount', {
                applicable: selection.pickable.value.length,
                total: selection.listed.value.length,
              })
            }}
          </span>
        </div>
        <span class="text-muted">
          {{ t('insightActions.select.showing', { count: selection.rows.value.length }) }}
        </span>
      </div>

      <InsightActionRowTable @retry-readiness="commands.refreshPreview()" />

      <p v-if="selection.elsewhere.value" class="m-0 text-xs text-muted">
        {{ t('insightActions.select.elsewhere', { count: selection.elsewhere.value.count }) }}
        <Button
          v-if="selection.elsewhere.value.target"
          variant="ghost"
          size="xs"
          data-testid="elsewhere-link"
          @click="store.setGroup(selection.elsewhere.value.target.key)"
        >
          {{ selection.elsewhere.value.target.label }}
        </Button>
        <span v-else>{{ t('insightActions.select.elsewhereUnnamed') }}</span>
      </p>
    </template>
  </div>
</template>
